import type {List} from 'immutable';

import client from 'apollo/client';
import {DiagnosticsListDocument} from 'client/AdaptiveDiagnostics/SkillPathsTab/queries/Diagnostics.queries.generated';
import type {
  DiagnosticsListQuery,
  DiagnosticsListQueryVariables
} from 'client/AdaptiveDiagnostics/SkillPathsTab/queries/Diagnostics.queries.generated';
import {getClassroomsWithStudents} from 'client/AdaptiveSubjects/AdaptiveSkillPath/getClassroomsWithStudents';
import type {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import type {StudentModelV2} from 'resources/augmented/Student/StudentModel.v2';

import type {ClassroomRef, DiagnosticRef, StudentRef} from '../types';

export interface CompletedStudent {
  student: StudentRef;
  classroom: ClassroomRef;
}

export interface CompletedRoster {
  students: CompletedStudent[];
  /** The picked diagnostics, with their names. */
  diagnostics: DiagnosticRef[];
}

const studentsOf = (classroom: ClassroomModelV1): StudentRef[] => {
  const students: List<StudentModelV2> | undefined = classroom.getStudents();
  return (students?.toArray() ?? []).map((student) => ({
    id: student.getId(),
    firstName: student.getFirstName(),
    lastName: student.getLastName()
  }));
};

const resultsIn = async (classroomId: string, diagnosticIds: string[]) => {
  const {data} = await client.query<DiagnosticsListQuery, DiagnosticsListQueryVariables>({
    query: DiagnosticsListDocument,
    variables: {
      classroomIds: [classroomId],
      isAllStatuses: false,
      includeDueDates: false,
      includeStartDates: false,
      filter: {
        id: {in: diagnosticIds},
        assignedToClassrooms: {eq: true, input: {classroomIds: [classroomId]}}
      },
      first: diagnosticIds.length
    }
  });
  const results = data?.diagnostics?.results ?? [];
  return {
    completedCount: Math.max(0, ...results.map((d) => d.countOfCompletedStudents)),
    diagnostics: results.map(({id, name}) => ({id, name}))
  };
};

/**
 * Who completed the diagnostics, one entry per student, and the diagnostics with their names. The
 * backend knows; the mock reads each class's completed count and takes that many students from the
 * top of the roster.
 */
export const completedStudentsOf = async (diagnosticIds: string[]): Promise<CompletedRoster> => {
  const classrooms = (await getClassroomsWithStudents()).toArray();
  const perClass = await Promise.all(
    classrooms.map(async (model) => {
      const classroom = {id: model.getId(), name: model.getName()};
      const {completedCount, diagnostics} = await resultsIn(classroom.id, diagnosticIds);
      const students = studentsOf(model)
        .slice(0, completedCount)
        .map((student) => ({student, classroom}));
      return {students, diagnostics};
    })
  );
  const found = new Map(perClass.flatMap(({diagnostics}) => diagnostics).map((d) => [d.id, d]));
  return {
    students: [
      ...new Map(
        perClass.flatMap(({students}) => students).map((entry) => [entry.student.id, entry])
      ).values()
    ],
    diagnostics: diagnosticIds.flatMap((id) => found.get(id) ?? [])
  };
};
